import React from 'react';
import ReactDOM from 'react-dom/client';
import './index.css';
import App from './App';
import { init, BrowserTracing } from '@sentry/browser';
import { isExpectedSignalRError, isSignalRConnectionClosedError, SIGNALR_TIMEOUT_MESSAGE, SIGNALR_CONNECTION_CLOSED_MESSAGE } from '@/lib/signalr';
import './polyfill';
import { AppProvider } from './providers/app';
import { ErrorBoundary } from './components/ErrorBoundary';
import { flushDeferredFaults } from '@/lib/deferredFaults';

// Dev and prod share one Sentry DSN. Without an explicit environment every
// event defaults to "production", so dev test events were indistinguishable
// from real screens. VITE_ENVIRONMENT is set per pipeline; MODE is the
// fallback for local runs.
const environment = (import.meta.env.VITE_ENVIRONMENT as string | undefined) ?? import.meta.env.MODE;

init({
  dsn: import.meta.env.VITE_SENTRY_DSN,
  environment,
  ignoreErrors: [SIGNALR_TIMEOUT_MESSAGE, SIGNALR_CONNECTION_CLOSED_MESSAGE],
  integrations: [
    new BrowserTracing({
      // Was /^http:\/\/signage-app-display.s3-website.eu-north-1.amazonaws.\.com/,
      // which pointed at the raw S3 endpoint over http and contained a stray
      // escaped dot, so it could never match the domain the displays actually use.
      tracePropagationTargets: [/^https:\/\/signypub\.inadra\.se/, /^https:\/\/(dev\.)?signyapi\.inadra\.se/],
    }),
  ],
  beforeSend(event, hint) {
    if (
      isExpectedSignalRError(hint.originalException) ||
      isExpectedSignalRError(hint.syntheticException) ||
      isSignalRConnectionClosedError(hint.originalException) ||
      isSignalRConnectionClosedError(hint.syntheticException) ||
      event.exception?.values?.some(v => isExpectedSignalRError(v?.value) || isSignalRConnectionClosedError(v?.value))
    ) {
      return null;
    }

    return event;
  },
});

// Anything that broke while this display was offline could not be reported at
// the time — the fault and the inability to report it share a cause. Post it
// now, while there is a network to post it over.
if (navigator.onLine) {
  flushDeferredFaults();
}

// And post it the moment connectivity comes back, rather than waiting for the
// next reload. A display that recovers on its own never reloads, so without
// this the evidence for why it broke would sit in localStorage until something
// unrelated restarted it — which on a signage screen can be weeks.
window.addEventListener('online', () => {
  flushDeferredFaults();
});

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <ErrorBoundary>
      <AppProvider>
        <App />
      </AppProvider>
    </ErrorBoundary>
  </React.StrictMode>
);
